/**
 * VoiceWidgetInTextChat — adapter that renders voice-persisted
 * `data-widget-*` parts inside the text chat surface (D-65, Plan 7b).
 *
 * The same voice widget components used in the live voice stage are
 * reused as-is, wrapped in `<VoiceThemeProvider>` so the brand-identity
 * `--voice-widget-*` CSS vars resolve correctly. Two persisted widget
 * types are intentionally NOT rendered:
 *   - `data-widget-tool-running` — ephemeral live-call cue.
 *   - `data-widget-clear` — stage-clear cue.
 * Both are no-ops in a persisted transcript.
 *
 * One widget (`PromptLoginWidget`) is rendered in `readOnly` mode here:
 * the destructive Cognito flow makes no sense in a replayed transcript,
 * so the prefill-driven effects and submit/resend buttons are disabled.
 */
import React from 'react';
import {
  CartWidget,
  CarouselsWidget,
  ComparisonWidget,
  ConfirmWidget,
  OrderDetailWidget,
  OrderListWidget,
  OrderWidget,
  ProductCardWidget,
  PromptLoginWidget,
  ReturnWidget,
  ReviewInsightsWidget,
  StoreListWidget,
  TicketCardWidget,
} from '../voice/components/widgets';
import { VoiceThemeProvider } from '../voice/theme';
import { MfWidgetLoader } from './ConciergeMessagePart/MfWidgetLoader';
import { mapVoiceWidgetToFederated } from './voiceToFederatedWidget';

type AnyWidgetComponent = React.ComponentType<{ data: any }>;

/**
 * Dispatch table — exported so unit tests can verify the mapping without
 * a full render path (the common-ui jest env is `node`, no jsdom). Each
 * `data-widget-*` persisted part-type renders the matching voice widget.
 */
export const TYPE_TO_COMPONENT: Record<string, AnyWidgetComponent> = {
  'data-widget-comparison': ComparisonWidget,
  'data-widget-cart': CartWidget,
  'data-widget-order': OrderWidget,
  'data-widget-order-detail': OrderDetailWidget,
  'data-widget-return': ReturnWidget,
  'data-widget-ticket': TicketCardWidget,
  'data-widget-product-card': ProductCardWidget,
  'data-widget-carousels': CarouselsWidget,
  'data-widget-review-insights': ReviewInsightsWidget,
  'data-widget-store-list': StoreListWidget,
  'data-widget-order-list': OrderListWidget,
  'data-widget-confirm': ConfirmWidget,
  'data-widget-prompt-login': PromptLoginWidget,
};

/**
 * Persisted widget types that are NEVER rendered in the text chat.
 * `data-widget-contact-card` has no FE renderer yet (D-73) — the narrator
 * already speaks the brand's email/phone, so we skip the part instead of
 * showing the neutral "Voice widget · …" placeholder.
 */
export const SKIPPED_TYPES = new Set<string>([
  'data-widget-tool-running',
  'data-widget-clear',
  'data-widget-contact-card',
]);

/** `data-widget-*` types for which the adapter applies `readOnly: true`. */
export const READ_ONLY_TYPES = new Set<string>(['data-widget-prompt-login']);

export interface VoiceWidgetInTextChatProps {
  type: string;
  data: unknown;
  /**
   * decisions/078 — when rendered inside the live voice overlay, the
   * prompt-login widget must NOT be read-only (the user has to actually
   * sign in) and the live `useVoiceSession` callbacks must be forwarded
   * so post-login retry fires. The post-end-call text chat omits this
   * prop and gets the default read-only behavior.
   */
  liveLoginCallbacks?: {
    onLoginCompleted?: () => void;
    onLoginFailed?: (reason: string) => void;
  };
  /** Live voice overlay — product-card Key Features fold after three bullets. */
  collapseProductCardFeatures?: boolean;
}

export const VoiceWidgetInTextChat: React.FC<VoiceWidgetInTextChatProps> = ({
  type,
  data,
  liveLoginCallbacks,
  collapseProductCardFeatures,
}) => {
  if (SKIPPED_TYPES.has(type)) return null;

  const Component = TYPE_TO_COMPONENT[type];

  // decisions/078 — center widgets horizontally inside their container so
  // both the live overlay scroll and the post-end-call text chat present
  // a centered card. Without this, widgets render at the bubble's left
  // edge because the parent assistant bubble uses `alignItems: 'left'`.
  // `overflow-x: auto` is kept (comparison table is wider than the
  // chat-column max-width and needs internal scroll).
  //
  // NOTE: this centered/overflow wrapper is only used for the bespoke voice
  // widgets (the `Component` / placeholder branches below). The federated
  // widgets use `federatedWrapperStyle` instead — see next comment.
  const wrapperStyle: React.CSSProperties = {
    width: '100%',
    overflowX: 'auto',
    marginTop: 8,
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'center',
  };

  // Federated widgets must render exactly like the native text-mode chat,
  // which mounts `<MfWidgetLoader>` directly in the message flow with no
  // wrapper — a plain, left-aligned, full-width block. The centered
  // `display: flex; justify-content: center; overflow-x: auto` wrapper above
  // puts the federated widget in a flex formatting context where it sizes to
  // its content rather than the column, which collapses the `compare-products`
  // action buttons into a vertical stack and makes voice widgets look
  // different from text mode (this is why bumping the column max-width alone
  // didn't help). A plain full-width block gives the federated widget the same
  // width it gets in text chat, so the layout matches in both modes.
  const federatedWrapperStyle: React.CSSProperties = {
    width: '100%',
    marginTop: 8,
    marginBottom: 8,
  };

  // Voice product widgets: render the TEXT-MODE federated widgets so the visual
  // matches the concierge chat surface (product-carousels / compare-products /
  // product-highlight). This applies in both the live voice overlay scroll and
  // the post-call text replay because both routes go through this adapter.
  const federated = mapVoiceWidgetToFederated(type, data);
  if (federated) {
    return (
      <div style={federatedWrapperStyle}>
        <MfWidgetLoader
          widgetId={federated.widgetId}
          toolInput={federated.toolInput}
          toolOutput={federated.toolOutput}
          partState="complete"
        />
      </div>
    );
  }

  if (!Component) {
    return (
      <div style={wrapperStyle}>
        <div
          style={{
            fontSize: 12,
            color: 'rgba(0,0,0,0.5)',
            fontStyle: 'italic',
            padding: '8px 12px',
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 8,
          }}
        >
          Voice widget · {type}
        </div>
      </div>
    );
  }

  // Default: prompt-login renders read-only in the post-end-call text
  // chat replay (Cognito flow makes no sense replayed). Override when
  // the live overlay supplies callbacks — the widget is interactive
  // (digits update, Submit + Resend visible, prefill effects fire).
  const extraProps: Record<string, unknown> = {};
  if (READ_ONLY_TYPES.has(type)) {
    if (type === 'data-widget-prompt-login' && liveLoginCallbacks) {
      extraProps.readOnly = false;
      extraProps.onLoginCompleted = liveLoginCallbacks.onLoginCompleted;
      extraProps.onLoginFailed = liveLoginCallbacks.onLoginFailed;
    } else {
      extraProps.readOnly = true;
    }
  }
  if (type === 'data-widget-product-card' && collapseProductCardFeatures) {
    extraProps.collapseKeyFeatures = true;
  }

  return (
    <div style={wrapperStyle}>
      <VoiceThemeProvider>
        <Component data={data as any} {...extraProps} />
      </VoiceThemeProvider>
    </div>
  );
};

export default VoiceWidgetInTextChat;
